import sha1 from 'crypto-js/sha1'
import {
  ColumnDataType,
  Language,
  Provider,
  TableSchema,
  WorkflowComponentParamType,
  WorkflowComponentValue,
  WorkflowComponentValueType,
  WorkflowVariable
} from '../types'

export function hashString(s: string | undefined) {
  return sha1(s || '')
    ?.toString()
    ?.slice(0, 16)
}

export function stringToValidTableName(s: string) {
  return s?.replace(/[\s.-]/g, '_')
}

export function tempTableName(component: string, param: unknown) {
  return `__temp_${hashString(`${component}:${param}`)}`
}

export const WORKFLOW_OUTPUT_TABLE_NAME_PARAMETER_NAME = '_outputtablename'
export const OUTPUT_TABLE_NAME_VAR_NAME = '__outputtable'
export const OUTPUT_TABLE_FQN_VAR_NAME = '__outputtablefqn'

export const IF_OUTPUT_NAME = 'if'
export const ELSE_OUTPUT_NAME = 'else'

export function isExpressionValue(value: WorkflowComponentValue | string) {
  if (typeof value === 'string') {
    return /^\s*\{\{.*\}\}\s*$/.test(value)
  } else if (
    value &&
    value.type === WorkflowComponentValueType.Literal &&
    value.value
  ) {
    return /^\s*\{\{.*\}\}\s*$/.test(`${value.value}`)
  } else {
    return false
  }
}

export function containsExpressionValue(
  value: WorkflowComponentValue | string
) {
  if (typeof value === 'string') {
    return /\{\{.*\}\}/.test(value)
  } else if (
    value &&
    value.type === WorkflowComponentValueType.Literal &&
    value.value
  ) {
    return /\{\{.*\}\}/.test(`${value.value}`)
  } else {
    return false
  }
}

export function isSingleVariable(value: WorkflowComponentValue) {
  if (
    value &&
    value.type === WorkflowComponentValueType.Literal &&
    value.value
  ) {
    return /^\{\{@[a-zA-Z_$][a-zA-Z_$0-9]*\}\}$/.test(`${value.value}`)
  }
  return false
}

export function getVariablesUsedInQuery(
  query: string,
  variables: Array<WorkflowVariable>
) {
  const regex = /@([a-zA-Z_$][a-zA-Z_$0-9]*)/g
  const matches = query.match(regex)
  let varNames: Array<string> = []
  if (matches) {
    varNames = matches.map((x) => x.replace('@', ''))
  }
  return (variables || [])
    .filter((v) => varNames.includes(v.name))
    .map((v) => v.name)
}

export function formatVariableValue(variable: WorkflowVariable) {
  return variable.type === WorkflowComponentParamType.String
    ? `'${(variable.value as string)?.replace(/'/g, "\\'") || ''}'`
    : `${variable.value}`
}

export function isNumericIntegerValue(inputValue) {
  if (typeof inputValue === 'string') {
    return !inputValue.includes('.')
  }
  return false
}

export function sortByName(a, b) {
  if (a.name > b.name) return 1
  if (a.name < b.name) return -1
  return 0
}

const DATE_REGEX = /^\d{4}\.(0[1-9]|1[012])\.(0[1-9]|[12]\d|3[01])$/
const SEMVER_REGEX =
  /^(0|[1-9]\d*)\.(0|[1-9]\d*)\.(0|[1-9]\d*)(?:-([0-9A-Za-z-]+(?:\.[0-9A-Za-z-]+)*))?(?:\+[0-9A-Za-z-]+)?$/

export function checkGreaterOrEqualATVersion(minVersion, currentVersion) {
  /*
    Versioning has changed, from:
    - Date format: `yyyy.mm.dd` to
    - Semver format: `major.minor.patch`
    So for example 0.1.0 is a version newer than 2022.12.15
  */
  if (DATE_REGEX.test(minVersion)) {
    if (DATE_REGEX.test(currentVersion)) {
      // NOTE: Support for legacy format `yyyy.mm.dd`
      return currentVersion >= minVersion
    } else if (SEMVER_REGEX.test(currentVersion)) {
      return true
    }
  } else if (SEMVER_REGEX.test(minVersion)) {
    if (DATE_REGEX.test(currentVersion)) {
      return false
    } else if (SEMVER_REGEX.test(currentVersion)) {
      // NOTE: Current format `major.minor.patch`
      if (currentVersion === minVersion) {
        return true
      }
      const minNumbers = minVersion.split('.')
      const currentNumbers = currentVersion.split('.')
      for (const [minNumber, currentNumber] of minNumbers.map((n, i) => [
        Number(n),
        Number(currentNumbers[i])
      ])) {
        if (currentNumber !== minNumber) {
          return currentNumber >= minNumber
        }
      }
    }
  }

  // Fallback: unknown version format
  return false
}

export function getValueTypeFromSchema(column: string, schema?: TableSchema) {
  let valueType
  if (schema) {
    valueType = schema.find((s) => s.name === column)?.type
  }
  return valueType
}

export function formatValue(
  value: string,
  connectionType: Provider,
  valueType?: string
) {
  switch (valueType) {
    case ColumnDataType.String:
      return `'${value.replace(/\\/g, '\\\\').replace(/'/g, "\\'")}'`
    case ColumnDataType.Date:
    case ColumnDataType.Datetime:
    case ColumnDataType.Time:
    case ColumnDataType.Boolean:
    case ColumnDataType.Timestamp:
      switch (connectionType) {
        case Provider.BigQuery:
        case Provider.Redshift:
          return `CAST ('${value}' AS ${valueType})`
        case Provider.Snowflake:
          return `TO_${(valueType as string).toUpperCase()}('${value}')`
        case Provider.Postgres:
          return `'${value}'::${valueType}`
      }
      break
    default:
      // Unknown type, so it tries to detect string values
      if (isNaN(Number(value))) {
        return `'${value.replace(/\\/g, '\\\\').replace(/'/g, "\\'")}'`
      }
      return value
  }
}

export function getDBTypesByProvider(
  provider?: Provider
): Record<string, unknown> {
  switch (provider) {
    case Provider.BigQuery:
      return {
        types: [
          'STRING',
          'NUMERIC',
          'BIGNUMERIC',
          'INT64',
          // 'INTERVAL',
          'FLOAT64',
          'BOOL',
          'DATE',
          'TIME',
          'DATETIME',
          'TIMESTAMP'
          // 'JSON',
          // 'BYTES',
        ]
      }
    case Provider.Snowflake:
      return {
        types: [
          'TEXT',
          'NUMBER',
          'FLOAT',
          'BOOLEAN',
          'DATE',
          'TIME',
          'TIMESTAMP'
          // 'TIMESTAMPTZ',
          // 'OBJECT',
          // 'ARRAY',
          // 'VARIANT',
          // 'BINARY',
        ]
      }
    case Provider.Redshift:
      return {
        types: [
          'CHAR',
          'VARCHAR',
          // 'INT2',
          'INT4',
          'INT8',
          'FLOAT4',
          'FLOAT8',
          'NUMERIC',
          'BOOL',
          'DATE',
          'TIME',
          // 'TIMETZ',
          'TIMESTAMP'
          // 'TIMESTAMPTZ',
          // 'VARBYTE',
        ]
      }
    case Provider.Postgres:
      return {
        types: [
          'CHAR',
          'VARCHAR',
          'TEXT',
          // 'INT2',
          'INT4',
          'INT8',
          // 'SERIAL2',
          // 'SERIAL4',
          // 'SERIAL8',
          'FLOAT4',
          'FLOAT8',
          'NUMERIC',
          'BOOL',
          'DATE',
          'TIME',
          // 'TIMETZ',
          'TIMESTAMP'
          // 'TIMESTAMPTZ',
          // 'JSON',
          // 'JSONB',
          // 'BYTEA',
        ]
      }
    case Provider.DatabricksRest:
      // Sub-set of types: https://docs.databricks.com/en/sql/language-manual/sql-ref-datatypes.html
      return {
        types: [
          'TINYINT',
          'SMALLINT',
          'INT',
          'BIGINT',
          'FLOAT',
          'DOUBLE',
          'DECIMAL',
          'DATE',
          'TIMESTAMP',
          'TIMESTAMP_NTZ',
          'STRING',
          'BOOLEAN'
        ]
      }
    default:
      return {}
  }
}

export function formatCodeComment(language: Language, s: string) {
  switch (language) {
    case Language.SQL:
      return `/*${s}*/\n`
    case Language.Python:
      return `# ${s}\n`
    default:
      return s
  }
}

export function nop(provider: Provider) {
  switch (provider) {
    case Provider.DatabricksRest:
      return 'pass'
    case Provider.Postgres:
      return 'PERFORM 1;'
    default:
      return 'SELECT 1;'
  }
}
